import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { List, X, FacebookLogo, InstagramLogo, XLogo, LinkedinLogo } from "@phosphor-icons/react";
import { SOCIAL_LINKS } from "@/lib/marketingSite";

const NAV_LINKS = [
  { to: "/features", label: "Features" },
  { to: "/ai-team", label: "AI Employees" },
  { to: "/industries", label: "Industries" },
  { to: "/pricing", label: "Pricing" },
  { to: "/stories", label: "Success Stories" },
  { to: "/blog", label: "Blog" },
];

export const Seo = ({ title, description, path = "" }) => {
  useEffect(() => {
    document.title = title;
    const set = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el && val) el.setAttribute(attr, val);
    };
    if (description) set('meta[name="description"]', "content", description);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `https://vozelto.com${path}`);
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:description"]', "content", description);
    set('meta[property="og:url"]', "content", `https://vozelto.com${path}`);
    set('meta[name="twitter:title"]', "content", title);
    set('meta[name="twitter:description"]', "content", description);
  }, [title, description, path]);
  return null;
};

export const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    el.style.transition = "opacity .6s ease, transform .6s ease";
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.style.opacity = "1"; el.style.transform = "translateY(0)"; io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

export const Reveal = ({ children, className = "" }) => {
  const ref = useReveal();
  return <div ref={ref} className={className}>{children}</div>;
};

const SOCIAL_ICONS = { Facebook: FacebookLogo, Instagram: InstagramLogo, X: XLogo, LinkedIn: LinkedinLogo };

export default function MarketingLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  useEffect(() => { setMenu(false); window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0D0D14] text-white grain-bg overflow-x-hidden">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0D0D14]/70 border-b border-white/5">
        <nav data-testid="marketing-nav" className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Vozelto home"><Logo variant="horizontal" height={30} showTagline={false} /></Link>
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={`text-sm px-3 py-2 transition-colors ${location.pathname.startsWith(l.to) ? "text-white" : "text-[#9a9a9a] hover:text-white"}`}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-signin" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex text-sm text-white hover:bg-white/5">Sign in</Button>
            <Button data-testid="nav-get-started" onClick={() => navigate("/signup")} className="text-sm bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-5">Start Free Trial</Button>
            <button data-testid="nav-mobile-toggle" onClick={() => setMenu((m) => !m)} aria-label="Menu"
              className="lg:hidden h-9 w-9 rounded-lg flex items-center justify-center text-[#9a9a9a] hover:text-white hover:bg-white/8">
              {menu ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </nav>
        {menu && (
          <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-white/5 bg-[#0a0a0a]/95 backdrop-blur-xl px-5 py-3">
            {[...NAV_LINKS, { to: "/faq", label: "FAQ" }, { to: "/contact", label: "Contact" }, { to: "/login", label: "Sign in" }].map((l) => (
              <Link key={l.to} to={l.to} className="block py-2.5 text-sm text-[#cfcfcf] hover:text-white">{l.label}</Link>
            ))}
          </div>
        )}
      </header>

      {children}

      <footer className="border-t border-white/5 bg-[#0a0a0a]" data-testid="marketing-footer">
        <div className="max-w-6xl mx-auto px-5 py-12 grid sm:grid-cols-2 md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <Logo variant="horizontal" height={30} showTagline={false} />
            <p className="mt-4 text-sm text-[#7a7a7a] leading-relaxed max-w-xs">Your complete AI marketing team. One platform. Generate an entire month of marketing in under 60 seconds.</p>
            <p className="mt-3 text-sm text-[#9a9a9a]">
              <a href="mailto:info@vozelto.com" data-testid="footer-email" className="hover:text-white">info@vozelto.com</a>
            </p>
            <div className="mt-4 flex items-center gap-2">
              {SOCIAL_LINKS.map((s) => {
                const Icon = SOCIAL_ICONS[s.name];
                return (
                  <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.name}
                    data-testid={`footer-social-${s.name.toLowerCase()}`}
                    className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center text-[#9a9a9a] hover:text-white hover:border-[#6B3DFF]/40 transition-colors">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>
          <div>
            <div className="label-caps mb-3">Product</div>
            <ul className="space-y-2 text-sm text-[#9a9a9a]">
              <li><Link to="/features" className="hover:text-white">Features</Link></li>
              <li><Link to="/ai-team" className="hover:text-white">AI Employees</Link></li>
              <li><Link to="/industries" className="hover:text-white">Industries</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/signup" data-testid="footer-signup" className="hover:text-white">Start Free Trial</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-caps mb-3">Resources</div>
            <ul className="space-y-2 text-sm text-[#9a9a9a]">
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><Link to="/faq" data-testid="footer-faq" className="hover:text-white">Documentation & FAQ</Link></li>
              <li><Link to="/stories" className="hover:text-white">Success Stories</Link></li>
              <li><Link to="/contact" data-testid="footer-support" className="hover:text-white">Support</Link></li>
              <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-caps mb-3">Company</div>
            <ul className="space-y-2 text-sm text-[#9a9a9a]">
              <li><Link to="/contact" data-testid="footer-contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms of Service</Link></li>
              <li><Link to="/data-deletion" data-testid="footer-data-deletion" className="hover:text-white">Data Deletion</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 text-center text-xs text-[#5a5a5a]">
          © {new Date().getFullYear()} Vozelto. All rights reserved. <span className="text-[#5a5a6a]" data-testid="footer-drezoro">Vozelto is a <Link to="/drezoro" className="hover:text-white underline underline-offset-2">Drezoro</Link> company.</span>
        </div>
      </footer>
    </div>
  );
}

export const PageHero = ({ caps, title, subtitle, children }) => (
  <section className="relative">
    <div className="absolute -top-24 -left-24 h-[24rem] w-[24rem] rounded-full bg-[#6B3DFF]/12 blur-[120px] pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-10 md:pt-24 md:pb-14">
      <div className="max-w-3xl fade-up">
        <div className="label-caps brand-text mb-3">{caps}</div>
        <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.08]">{title}</h1>
        {subtitle && <p className="mt-5 text-base md:text-lg text-[#b5b5b5] max-w-2xl leading-relaxed">{subtitle}</p>}
        {children}
      </div>
    </div>
  </section>
);

export const FinalCta = ({ title = "Ready to Build Your AI Marketing Team?", sub = "14-day free trial. Cancel anytime. Your first campaign can be ready within the hour." }) => {
  const navigate = useNavigate();
  return (
    <section className="max-w-6xl mx-auto px-5 pb-20 pt-8">
      <Reveal>
        <div className="card-matte brand-glow p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-60 w-60 rounded-full bg-[#6B3DFF]/20 blur-[100px] pointer-events-none" />
          <h2 className="relative font-head font-extrabold text-3xl md:text-4xl tracking-tight">{title}</h2>
          <p className="relative mt-4 text-[#b5b5b5] max-w-xl mx-auto">{sub}</p>
          <Button data-testid="page-final-cta" onClick={() => navigate("/signup")}
            className="relative mt-7 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-8 h-12 text-base">
            Start Free Trial
          </Button>
        </div>
      </Reveal>
    </section>
  );
};
